import { forwardRef, useEffect, useImperativeHandle, useState } from "react";

export type FluidCursorHandle = {
  snapTo: (x: number, y: number) => void;
};

interface PointerState {
  x: number;
  y: number;
  isFine: boolean;
  isVisible: boolean;
  isInteractive: boolean;
}

const CROSSHAIR_RADIUS = 10;

export const FluidCursor = forwardRef<FluidCursorHandle>((_, ref) => {
  const [pointer, setPointer] = useState<PointerState>({
    x: -120,
    y: -120,
    isFine: false,
    isVisible: false,
    isInteractive: false,
  });

  useImperativeHandle(
    ref,
    () => ({
      snapTo: (x, y) => {
        setPointer((current) => ({ ...current, x, y, isVisible: true }));
      },
    }),
    [],
  );

  useEffect(() => {
    const media = window.matchMedia("(pointer: fine)");
    const updateFinePointer = () => {
      setPointer((current) => ({ ...current, isFine: media.matches }));
    };
    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerType && event.pointerType !== "mouse") return;
      const element = event.target instanceof Element ? event.target : null;
      const isInteractive = Boolean(
        element?.closest(
          "button,input,textarea,select,a,[role='button'],[contenteditable='true']",
        ),
      );
      setPointer({
        x: event.clientX,
        y: event.clientY,
        isFine: media.matches,
        isVisible: true,
        isInteractive,
      });
    };
    const handlePointerLeave = () => {
      setPointer((current) => ({ ...current, isVisible: false }));
    };

    updateFinePointer();
    media.addEventListener("change", updateFinePointer);
    window.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("mouseleave", handlePointerLeave);

    return () => {
      media.removeEventListener("change", updateFinePointer);
      window.removeEventListener("pointermove", handlePointerMove);
      document.removeEventListener("mouseleave", handlePointerLeave);
    };
  }, []);

  if (!pointer.isFine || !pointer.isVisible || pointer.isInteractive)
    return null;

  return (
    <div
      aria-hidden="true"
      className="auth-crosshair-cursor"
      style={{ transform: `translate3d(${pointer.x}px, ${pointer.y}px, 0)` }}
    >
      <style>{crosshairCss}</style>
    </div>
  );
});

FluidCursor.displayName = "FluidCursor";

const crosshairCss = `
  .auth-crosshair-cursor {
    position: fixed;
    left: 0;
    top: 0;
    z-index: 20;
    width: 0;
    height: 0;
    pointer-events: none;
    mix-blend-mode: screen;
    will-change: transform;
  }

  .auth-crosshair-cursor::before,
  .auth-crosshair-cursor::after {
    content: "";
    position: absolute;
    background: color-mix(in srgb, var(--fintheon-text, #f0ead6) 76%, var(--fintheon-primary, var(--fintheon-accent)) 24%);
    box-shadow: 0 0 8px color-mix(in srgb, var(--fintheon-primary, var(--fintheon-accent)) 28%, transparent);
  }

  .auth-crosshair-cursor::before {
    left: -${CROSSHAIR_RADIUS}px;
    top: -0.5px;
    width: ${CROSSHAIR_RADIUS * 2}px;
    height: 1px;
  }

  .auth-crosshair-cursor::after {
    left: -0.5px;
    top: -${CROSSHAIR_RADIUS}px;
    width: 1px;
    height: ${CROSSHAIR_RADIUS * 2}px;
  }
`;
